// Auth context for the web client (spec #34). Mirrors the mobile
// `AuthController` (StateNotifier + 4-state machine) but expressed as
// a `useReducer`-backed React context so the rest of the tree can
// read it via the `useAuth` hook.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { authApi } from '@/api/authApi';
import { ApiException } from '@/api/errors';
import { clearStoredSession, readStoredSession, writeStoredSession } from '@/api/tokenStorage';
import type { AuthSession } from '@/api/types';
import { setDefaultUnauthorizedHandler } from '@/api/http';

export type AuthStatus = 'unknown' | 'unauthenticated' | 'authenticated' | 'refreshing';

export interface AuthState {
  status: AuthStatus;
  session: AuthSession | null;
  errorMessage: string | null;
  errorCode: string | null;
}

type AuthAction =
  | { type: 'restore-start' }
  | { type: 'restore-success'; session: AuthSession }
  | { type: 'restore-failure'; message: string; code: string | null }
  | { type: 'login-success'; session: AuthSession }
  | { type: 'login-failure'; message: string; code: string | null }
  | { type: 'refresh-start' }
  | { type: 'refresh-success'; session: AuthSession }
  | { type: 'refresh-failure'; message: string; code: string | null }
  | { type: 'logout' };

const initialState: AuthState = {
  status: 'unknown',
  session: null,
  errorMessage: null,
  errorCode: null,
};

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'restore-start':
      return { ...state, status: 'unknown' };
    case 'restore-success':
    case 'login-success':
    case 'refresh-success':
      return {
        status: 'authenticated',
        session: action.session,
        errorMessage: null,
        errorCode: null,
      };
    case 'refresh-start':
      return { ...state, status: 'refreshing' };
    case 'restore-failure':
    case 'login-failure':
    case 'refresh-failure':
      return {
        status: 'unauthenticated',
        session: null,
        errorMessage: action.message,
        errorCode: action.code,
      };
    case 'logout':
      return { status: 'unauthenticated', session: null, errorMessage: null, errorCode: null };
    default:
      return state;
  }
}

export interface AuthContextValue {
  state: AuthState;
  session: AuthSession | null;
  isAuthenticated: boolean;
  /** True while an access-token refresh is in flight. During this window the
   *  user is still logged in — RequireAuth must NOT redirect to /login. */
  isRefreshing: boolean;
  getToken: () => string | null;
  restore: () => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<AuthSession>;
  register: (input: { username: string; email: string; password: string }) => Promise<AuthSession>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (input: { token: string; new_password: string }) => Promise<void>;
  verifyEmail: (token: string) => Promise<boolean>;
  /** Silent access-token refresh. No-op if already valid. */
  refreshIfNeeded: () => Promise<void>;
  /**
   * Refresh the access token and return the new value. Returns `null`
   * when no session exists or the refresh fails. Concurrent calls share
   * a single in-flight refresh request.
   */
  refreshAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const getToken = useCallback(() => stateRef.current.session?.accessToken ?? null, []);

  const persist = useCallback((session: AuthSession) => {
    writeStoredSession(session);
  }, []);

  const restore = useCallback(async () => {
    dispatch({ type: 'restore-start' });
    const stored = readStoredSession();
    if (!stored) {
      dispatch({ type: 'restore-failure', message: 'No persisted session.', code: null });
      return;
    }
    const expiresAt = new Date(stored.expiresAt).getTime();
    if (Number.isFinite(expiresAt) && Date.now() < expiresAt - 30_000) {
      dispatch({ type: 'restore-success', session: stored });
      return;
    }
    try {
      dispatch({ type: 'refresh-start' });
      const refreshed = await authApi.refresh(stored.refreshToken);
      persist(refreshed);
      dispatch({ type: 'refresh-success', session: refreshed });
    } catch (e) {
      clearStoredSession();
      const err = e instanceof ApiException ? e : null;
      dispatch({
        type: 'refresh-failure',
        message: err?.message ?? 'Session expired.',
        code: err?.code ?? null,
      });
    }
  }, [persist]);

  const login = useCallback<AuthContextValue['login']>(
    async (input) => {
      const session = await authApi.login(input);
      persist(session);
      dispatch({ type: 'login-success', session });
      return session;
    },
    [persist],
  );

  const register = useCallback<AuthContextValue['register']>(
    async (input) => {
      const session = await authApi.register(input);
      persist(session);
      dispatch({ type: 'login-success', session });
      return session;
    },
    [persist],
  );

  const logout = useCallback(async () => {
    const current = stateRef.current.session;
    if (current) {
      try {
        await authApi.logout(current.refreshToken);
      } catch {
        // best-effort; the server has its own TTL for orphaned sessions
      }
    }
    clearStoredSession();
    dispatch({ type: 'logout' });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await authApi.forgotPassword(email);
  }, []);

  const resetPassword = useCallback<AuthContextValue['resetPassword']>(async (input) => {
    await authApi.resetPassword(input);
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    return await authApi.verifyEmail(token);
  }, []);

  const refreshIfNeeded = useCallback(async () => {
    const current = stateRef.current.session;
    if (!current) return;
    const expiresAt = new Date(current.expiresAt).getTime();
    if (Number.isFinite(expiresAt) && Date.now() < expiresAt - 30_000) return;
    try {
      dispatch({ type: 'refresh-start' });
      const refreshed = await authApi.refresh(current.refreshToken);
      persist(refreshed);
      dispatch({ type: 'refresh-success', session: refreshed });
    } catch (e) {
      clearStoredSession();
      const err = e instanceof ApiException ? e : null;
      dispatch({
        type: 'refresh-failure',
        message: err?.message ?? 'Session expired.',
        code: err?.code ?? null,
      });
    }
  }, [persist]);

  // In-flight refresh deduplication. Multiple parallel 401s (e.g. five
  // concurrent screen mounts after a stale tab resumes) must trigger
  // exactly one /auth/refresh round-trip — not five.
  const inFlightRefreshRef = useRef<Promise<string | null> | null>(null);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // Reuse the existing promise if one is already in flight.
    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current;
    }
    const current = stateRef.current.session;
    if (!current) return null;
    const promise = (async () => {
      try {
        const refreshed = await authApi.refresh(current.refreshToken);
        persist(refreshed);
        dispatch({ type: 'refresh-success', session: refreshed });
        return refreshed.accessToken;
      } catch {
        clearStoredSession();
        dispatch({
          type: 'refresh-failure',
          message: 'Session expired.',
          code: null,
        });
        return null;
      } finally {
        inFlightRefreshRef.current = null;
      }
    })();
    inFlightRefreshRef.current = promise;
    return promise;
  }, [persist]);

  // Install / remove the reactive 401 hook so any fetchWithAuth call
  // site benefits from the dedup'd refresh path. The handler resolves
  // with the new access token on success or `null` if the session is
  // unrecoverable — `fetchWithAuth` then retries the original request
  // exactly once and gives up on the second 401.
  useEffect(() => {
    setDefaultUnauthorizedHandler(refreshAccessToken);
    return () => setDefaultUnauthorizedHandler(null);
  }, [refreshAccessToken]);

  // Auto-refresh when the access token is about to expire.
  useEffect(() => {
    if (state.status !== 'authenticated' || !state.session) return;
    const expiresAt = new Date(state.session.expiresAt).getTime();
    if (!Number.isFinite(expiresAt)) return;
    const delay = Math.max(5_000, expiresAt - Date.now() - 30_000);
    const t = window.setTimeout(() => {
      void refreshIfNeeded();
    }, delay);
    return () => window.clearTimeout(t);
  }, [state.session, state.status, refreshIfNeeded]);

  // Restore session from sessionStorage on first mount.
  useEffect(() => {
    void restore();
  }, [restore]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      session: state.session,
      isAuthenticated: state.status === 'authenticated' && state.session !== null,
      isRefreshing: state.status === 'refreshing',
      getToken,
      restore,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      refreshIfNeeded,
      refreshAccessToken,
    }),
    [
      state,
      getToken,
      restore,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      refreshIfNeeded,
      refreshAccessToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
